<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import {DsoContract, useDsoContractStore} from "@/stores/dsoContractStore";
import router from "@/router/routerSetup";
import {useRoute} from "vue-router";
import {useDsoInformationStore} from "@/stores/dsoCompanyInformationStore";
import { useSnackbarStore } from "@es-bill/es-bill-common-components";
import { useI18n } from 'vue-i18n';
import { PrimaryButton } from '@es-bill/es-bill-common-components';

const communitiesStore = useCommunitiesStore();
const dsoContractStore = useDsoContractStore();
const dsoInformationStore = useDsoInformationStore();
const route = useRoute();
const $log = inject('log');
const snackbarStore = useSnackbarStore();
const { t } = useI18n();

const valid = ref(false);
const form = ref(null);
const community = ref(null);
const gridOperator = ref(null);
const digitalContract = ref(false);
const formValidation = inject('$formValidation');
const validateGridOperator = value => digitalContract.value || formValidation.formValidationRequired(value);

const availableCommunities = computed(() => communitiesStore.allCommunities.map(c => ({...c, props: {subtitle: c.ecn + ' / ' + c.ecid}})));
const operatorsAvailable = computed(() => dsoInformationStore.informations.map(c => ({...c, props: {subtitle: c.ecn }})));

watch(() => route.params.id, loadExistingContract);
onMounted(() => {
  dsoInformationStore.fetchDsoInformation();
  loadExistingContract(route.params.id);
});

const existingContract = ref(undefined);

function loadExistingContract(id) {
  if (id) {
    dsoContractStore.getContractById(id).then(contract => {
      console.log('contract', contract)
      existingContract.value = contract;
      community.value = communitiesStore.allCommunities.find(c => c.ecn === contract.ecn);
      digitalContract.value = contract.digitalContract === true;

      // Ensure DSO information is loaded before trying to find the grid operator
      if (dsoInformationStore.informations.length === 0) {
        dsoInformationStore.fetchDsoInformation().then(() => {
          gridOperator.value = dsoInformationStore.informations.find(i => i.ecn === contract.ecnDso);
        });
      } else {
        gridOperator.value = dsoInformationStore.informations.find(i => i.ecn === contract.ecnDso);
      }
    });
  }
}

const validateUrl = value => {
  const pattern = /^https:\/\/(?:[\w-]{2,}\.)+[\w-]{2,}(\/[\w-.,@?^=%&:\/~+#]*[\w-@?^=%&\/~+#])?$/;
  return pattern.test(value) || 'Enter a valid URL that starts with https://';
};
const submitForm = async () => {
  if (form.value.validate()) {
    // a digital contract is not bound to one DSO
    const operator = digitalContract.value ? null : gridOperator.value;
    const contract = new DsoContract(
      community.value.name,
      community.value.ecn,
      community.value.ecid,
      operator?.name,
      operator?.ecn,
      undefined,
      undefined,
      digitalContract.value
    );

    try {
      if (existingContract.value) {
        await dsoContractStore.updateContract(existingContract.value._id, contract);
        snackbarStore.showSnackbar(t('applicantMessages.updateSuccess'), '', 'Success', 4000, []);
      } else {
        await dsoContractStore.createContract(contract);
        snackbarStore.showSnackbar(t('requestSuccess'), '', 'Success', 4000, []);
      }

      // navigate to dsoContracts
      router.push({name: 'dsoContracts'});
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.message || t('unexpectedError'), '', 'Error', 10000, []);
    }
  }
}
</script>

<template>
  <v-container>
    <v-form ref="form" v-model="valid">
      <h1 class="mb-10">{{ $t('createDsoContracts') }}</h1>

      <v-autocomplete
        bg-color="white"
        :label="$t('selectCommunity')"
        v-model="community"
        :items="availableCommunities"
        :filter-keys="['title','props.subtitle']"
        variant="outlined"
        item-value="name"
        item-title="name"
        return-object
        clearable
        :rules="[formValidation.formValidationRequired]"
      ></v-autocomplete>

      <v-checkbox
        :label="$t('digitalContract')"
        v-model="digitalContract"
        color="primary"
        hide-details
        class="mb-4"
      ></v-checkbox>
<!-- TODO:subtext ecn + ecid + per ecid anzeigen nicht per ecn-->
      <v-autocomplete
        v-if="!digitalContract"
        bg-color="white"
        :label="$t('selectGridOperator')"
        v-model="gridOperator"
        :items="operatorsAvailable"
        :filter-keys="['title','props.subtitle']"
        variant="outlined"
        item-value="name"
        item-title="name"
        return-object
        clearable
        :rules="[validateGridOperator]"
      ></v-autocomplete>

      <PrimaryButton :disabled="!valid" @click="submitForm" :text="$t('submit')" />
    </v-form>
  </v-container>
</template>

<style scoped>
/* Optional styling */
</style>
